<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn
          color="success"
          dark
          class="mb-4 mx-4"
          v-on="on"
          depressed
          small
          @click="createMenu"
        >New menu</v-btn>
      </template>
      <span>Create a new menu</span>
    </v-tooltip>

    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>
          <span class="title">{{ formTitle }}</span>
        </v-card-title>

        <v-form v-model="drawer.valid">
          <v-card-text>
            <component
              v-for="(field, index) in formFields"
              :is="`${field.type}-field`"
              :key="index"
              :field="field"
              :value.sync="formData[index]"
            ></component>
          </v-card-text>
        </v-form>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            text
            :disabled="!drawer.valid"
            :loading="drawer.loading"
            @click="save"
          >Save</v-btn>

          <v-btn color="error" text :loading="drawer.loading" @click="closeDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-divider></v-divider>

    <v-expansion-panels
      accordion
      hover
      tile
      flat
      class="elevation-0"
      v-if="items.length"
      focusable
      :value="activeMenuPanel"
      @change="setActiveMenuPanel"
    >
      <v-expansion-panel v-for="item in items" :key="item.id" style="margin-right: 2px">
        <v-expansion-panel-header style="min-height:0" class="subtitle-2">{{ item.name }}</v-expansion-panel-header>

        <v-expansion-panel-content>
          <div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  color="success"
                  icon
                  depressed
                  small
                  @click="createMenuItem(item.id)"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>
                Add a new item to
                <span class="text-lowercase">{{ item.name }}</span>
              </span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon small color="accent" @click="editMenu(item)">
                  <v-icon :fill="$vuetify.theme.defaults.dark.accent" style="width: 18">$edit</v-icon>
                </v-btn>
              </template>
              <span>Edit menu</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon small color="error" @click="deleteMenu(item.id)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete menu</span>
            </v-tooltip>
          </div>

          <menu-items-table
            :items="item.parent_items"
            :menuID="item.id"
            :ref="`menuItems${item.id}`"
          ></menu-items-table>
        </v-expansion-panel-content>

        <v-divider></v-divider>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card v-else flat>
      <v-card-text>No menus available.</v-card-text>
    </v-card>
  </div>
</template>

<script>
import MenuItemsTable from '@/components/editor/types/PageNavigation/MenuItemsTable'
import { upsertMenu, deleteMenu } from '@/graphql/menu.gql'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'MenusTable',
  props: {
    templateID: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  components: {
    MenuItemsTable
  },
  data: () => ({
    dialog: false,
    formData: {
      id: null,
      name: ''
    },
    defaultFormData: {
      id: null,
      name: ''
    },
    formFields: {
      name: {
        label: 'Name',
        type: 'text',
        rules: ['required']
      }
    }
  }),
  computed: {
    formTitle() {
      return this.formData.id === null ? 'New menu' : 'Edit menu'
    },
    ...mapState('editor', ['drawer', 'activeMenuPanel'])
  },
  methods: {
    ...mapActions('editor', ['saveChanges', 'confirmAndSaveChanges']),
    closeDialog() {
      this.dialog = false
      this.formData = Object.assign({}, this.defaultFormData)
    },
    createMenu() {
      this.formData = Object.assign({}, this.defaultFormData)

      this.dialog = true
    },
    save() {
      this.saveChanges({
        mutation: upsertMenu,
        variables: {
          data: {
            ...this.formData,
            template_id: this.templateID
          }
        },
        messages: {
          success: this.formData.id
            ? 'Menu successfully updated.'
            : 'Menu successfully created.'
        },
        refetch: true,
        close: false
      })
    },
    editMenu(data) {
      this.formData = Object.assign(
        {},
        {
          id: data.id,
          name: data.name
        }
      )

      this.dialog = true
    },
    deleteMenu(id) {
      this.confirmAndSaveChanges({
        mutation: deleteMenu,
        variables: { id },
        messages: {
          confirm:
            'Items assigned to this menu will be deleted as well. This will also create conflicts with sections using this menu.',
          success: 'Menu successfully deleted.'
        },
        refetch: true,
        close: false
      })
    },
    createMenuItem(id) {
      let refText = `menuItems${id}`

      this.$refs[refText][0].createMenuItem()
    },
    setActiveMenuPanel(value) {
      this.$store.state.editor.activeMenuPanel = value
    }
  }
}
</script>
